export type PhaseOneItemStatus = "ready" | "partial" | "missing" | "review";

export interface PhaseOneAnalysisItem {
  id: "identity" | "form-strength" | "reference" | "pk" | "guidance" | "traceability";
  label: string;
  status: PhaseOneItemStatus;
  conclusion: string;
  evidence?: string;
}

export interface PhaseOneAnalysis {
  readinessScore: number;
  overallStatus: "complete" | "partial" | "insufficient";
  summary: string;
  evidenceCount: number;
  items: PhaseOneAnalysisItem[];
  nextActions: string[];
  limitations: string[];
}

export interface PhaseOneIdentityItem {
  label: string;
  value?: string;
}

export interface PhaseOneApprovalRow {
  agency: string;
  product?: string;
  ingredient?: string;
  strength?: string;
  href?: string;
}

export interface PhaseOneEvidenceLink {
  source: string;
  type: string;
  title: string;
  href?: string;
  note?: string;
}

export interface PhaseOneAnalysisInput {
  drugName: { cn: string; en: string; inn: string };
  dosageForm: string;
  isCombination?: boolean;
  components?: Array<{ cn: string; en: string }>;
  identityItems?: PhaseOneIdentityItem[];
  approvalRows?: PhaseOneApprovalRow[];
  evidenceLinks?: PhaseOneEvidenceLink[];
  pkParams: {
    cmax?: string;
    auc?: string;
    tmax?: string;
    t12?: string;
    bioavailability?: string;
    vd?: string;
    cl?: string;
    proteinBinding?: string;
  };
}

function clean(value: unknown): string {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  return text && text !== "—" && text !== "-" ? text : "";
}

function identityValue(items: PhaseOneIdentityItem[], labelPattern: RegExp): string {
  return clean(items.find((item) => labelPattern.test(item.label))?.value);
}

function item(
  id: PhaseOneAnalysisItem["id"],
  label: string,
  status: PhaseOneItemStatus,
  conclusion: string,
  evidence?: string,
): PhaseOneAnalysisItem {
  return { id, label, status, conclusion, evidence: clean(evidence) || undefined };
}

function uniqueSourceCount(links: PhaseOneEvidenceLink[], rows: PhaseOneApprovalRow[]): number {
  const sources = new Set<string>();
  links.forEach((link) => {
    if (clean(link.href)) sources.add(clean(link.source).toLowerCase());
  });
  rows.forEach((row) => {
    if (clean(row.href)) sources.add(clean(row.agency).toLowerCase());
  });
  return sources.size;
}

export function buildPhaseOneAnalysis(input: PhaseOneAnalysisInput): PhaseOneAnalysis {
  const identityItems = input.identityItems || [];
  const approvalRows = input.approvalRows || [];
  const evidenceLinks = input.evidenceLinks || [];
  const components = (input.components || []).filter((component) => clean(component.cn) || clean(component.en));
  const analysisItems: PhaseOneAnalysisItem[] = [];
  const nextActions: string[] = [];
  let score = 0;

  const cnName = clean(input.drugName.cn);
  const englishName = clean(input.drugName.inn) || clean(input.drugName.en);
  if (input.isCombination) {
    if (cnName && englishName && components.length >= 2) {
      score += 20;
      analysisItems.push(item(
        "identity",
        "药品身份与复方成分",
        "ready",
        "中英文名称及复方活性成分已拆分，可分别路由至中文和国际数据源。",
        components.map((component) => clean(component.en) || clean(component.cn)).join(" + "),
      ));
    } else {
      score += cnName && englishName ? 10 : 5;
      analysisItems.push(item(
        "identity",
        "药品身份与复方成分",
        "review",
        "已识别为复方制剂，但活性成分拆分不完整，跨数据库检索可能漏项。",
      ));
      nextActions.push("核对复方中每个活性成分的中文通用名、英文 INN 及盐型/水合物信息。");
    }
  } else if (cnName && englishName) {
    score += 20;
    analysisItems.push(item(
      "identity",
      "药品身份",
      "ready",
      "中文检索名与英文/INN 均已建立，可用于中英文数据源交叉核对。",
      `${cnName} / ${englishName}`,
    ));
  } else {
    score += cnName || englishName ? 8 : 0;
    analysisItems.push(item(
      "identity",
      "药品身份",
      cnName || englishName ? "partial" : "missing",
      "中英文药名映射不完整，不能确认各监管来源检索的是同一活性成分。",
    ));
    nextActions.push("补齐并人工核对中文通用名、英文 INN、盐型/酯型及常用商品名。");
  }

  const dosageForm = clean(input.dosageForm) || identityValue(identityItems, /剂型/);
  const strength = identityValue(identityItems, /规格/)
    || approvalRows.map((row) => clean(row.strength)).filter(Boolean).join("、");
  if (dosageForm && strength) {
    score += 15;
    analysisItems.push(item(
      "form-strength",
      "剂型、给药途径与规格",
      "ready",
      "已获得剂型和上市规格，可用于筛选同剂型参比制剂及匹配指导原则。",
      `${dosageForm}；${strength}`,
    ));
  } else if (dosageForm || strength) {
    score += 7;
    analysisItems.push(item(
      "form-strength",
      "剂型、给药途径与规格",
      "partial",
      "剂型或规格信息不完整，尚不足以完成处方规格和给药条件核对。",
      dosageForm || strength,
    ));
    nextActions.push("从最新版官方说明书补齐剂型、给药途径及全部在售规格。");
  } else {
    analysisItems.push(item(
      "form-strength",
      "剂型、给药途径与规格",
      "missing",
      "未获得可核对的剂型和规格信息。",
    ));
    nextActions.push("从监管机构说明书或批准数据库确认剂型、给药途径和全部上市规格。");
  }

  const applicationNumbers = identityValue(identityItems, /FDA\s*申请号/i);
  const hasCdeReference = approvalRows.some((row) => /参比制剂/i.test(clean(row.agency)));
  const hasOriginatorApplication = /\b(?:NDA|BLA)\s*\d+/i.test(applicationNumbers)
    || evidenceLinks.some((link) => /\b(?:NDA|BLA)\s*\d+/i.test(`${clean(link.note)} ${clean(link.title)}`));
  if (hasCdeReference) {
    score += 20;
    analysisItems.push(item(
      "reference",
      "原研 / 参比制剂线索",
      "ready",
      "已发现 CDE 参比制剂记录，可作为参比制剂核查的首要官方线索。",
      applicationNumbers,
    ));
  } else if (hasOriginatorApplication) {
    score += 12;
    analysisItems.push(item(
      "reference",
      "原研 / 参比制剂线索",
      "partial",
      "已发现 FDA 原始申请线索，但仍需核对 RLD/RS、持证商和目标市场参比制剂身份。",
      applicationNumbers,
    ));
    nextActions.push("核对 FDA Orange Book 的 RLD/RS 标识及目标市场参比制剂目录。");
  } else {
    analysisItems.push(item(
      "reference",
      "原研 / 参比制剂线索",
      "missing",
      "当前结果不足以确认原研或参比制剂身份。",
      applicationNumbers,
    ));
    nextActions.push("补查 CDE 参比制剂目录、FDA Orange Book 或其他目标市场官方参比依据。");
  }

  const cmax = clean(input.pkParams.cmax);
  const auc = clean(input.pkParams.auc);
  const secondaryPk = [
    input.pkParams.tmax,
    input.pkParams.t12,
    input.pkParams.bioavailability,
    input.pkParams.vd,
    input.pkParams.cl,
    input.pkParams.proteinBinding,
  ].map(clean).filter(Boolean);
  if (cmax && auc) {
    score += 25;
    analysisItems.push(item(
      "pk",
      "关键 PK / BE 终点证据",
      "ready",
      "已检出 Cmax 与 AUC 数值线索，可用于早期资料核查；正式样本量与变异估计仍应回到原始研究。",
      `Cmax: ${cmax}；AUC: ${auc}`,
    ));
  } else if (cmax || auc) {
    score += 14;
    analysisItems.push(item(
      "pk",
      "关键 PK / BE 终点证据",
      "partial",
      "仅检出一个主要暴露终点，不能支持完整的 BE/PK 参数框架。",
      cmax ? `Cmax: ${cmax}` : `AUC: ${auc}`,
    ));
    nextActions.push("从临床药理审评、说明书或文献补齐 Cmax、AUC0-t、AUC0-inf 及个体内变异信息。");
  } else if (secondaryPk.length > 0) {
    score += 8;
    analysisItems.push(item(
      "pk",
      "关键 PK / BE 终点证据",
      "partial",
      "已获得描述性 PK 参数，但尚无可用于主要 BE 终点核查的 Cmax/AUC 数值。",
      `${secondaryPk.length} 项描述性参数`,
    ));
    nextActions.push("优先补查 Cmax、AUC0-t、AUC0-inf、Tmax 及个体内 CV，不以说明书摘要替代原始研究数据。");
  } else {
    analysisItems.push(item(
      "pk",
      "关键 PK / BE 终点证据",
      "missing",
      "未检出可表格化的主要或描述性 PK 参数。",
    ));
    nextActions.push("补查 Cmax、AUC0-t、AUC0-inf、Tmax、个体内 CV 及其原始研究来源。");
  }

  const guidancePattern = /bioequivalence|biopharmaceutics|product[- ]specific|\bPSG\b|BE\s*指导|生物等效|指导原则/i;
  const guidanceLinks = evidenceLinks.filter((link) => guidancePattern.test(
    `${clean(link.source)} ${clean(link.type)} ${clean(link.title)}`,
  ));
  const linkedGuidance = guidanceLinks.filter((link) => clean(link.href));
  if (linkedGuidance.length > 0) {
    score += 15;
    analysisItems.push(item(
      "guidance",
      "BE / 品种指导原则",
      "ready",
      "已获得可打开的品种或生物等效性指导文件，应优先据此核对研究类型和关键设计条件。",
      linkedGuidance.slice(0, 2).map((link) => clean(link.title)).join("；"),
    ));
  } else if (guidanceLinks.length > 0) {
    score += 7;
    analysisItems.push(item(
      "guidance",
      "BE / 品种指导原则",
      "partial",
      "发现指导原则记录，但缺少可直接打开的官方文件链接。",
      guidanceLinks[0]?.title,
    ));
    nextActions.push("在 FDA PSG、CDE 指导原则或目标市场指南中核实最新版原文链接。");
  } else {
    analysisItems.push(item(
      "guidance",
      "BE / 品种指导原则",
      "missing",
      "当前结果未检出品种专属或通用 BE 指导原则。",
    ));
    nextActions.push("检索 FDA PSG、CDE/EMA/Health Canada 相关指南并确认是否要求空腹、餐后或其他特定研究。");
  }

  const sourceCount = uniqueSourceCount(evidenceLinks, approvalRows);
  if (sourceCount >= 2) {
    score += 5;
    analysisItems.push(item(
      "traceability",
      "官方证据可追溯性",
      "ready",
      "至少两个独立监管来源提供了可回溯链接，便于交叉核对版本和结论。",
      `${sourceCount} 个可追溯来源`,
    ));
  } else if (sourceCount === 1) {
    score += 2;
    analysisItems.push(item(
      "traceability",
      "官方证据可追溯性",
      "partial",
      "目前仅有一个可回溯监管来源，跨市场结论仍需复核。",
      "1 个可追溯来源",
    ));
  } else {
    analysisItems.push(item(
      "traceability",
      "官方证据可追溯性",
      "missing",
      "未获得可直接打开的官方原文链接。",
    ));
    nextActions.push("补齐官方说明书、审评报告或指导原则链接，并记录文件日期/版本。");
  }

  const boundedScore = Math.max(0, Math.min(100, score));
  const readyCount = analysisItems.filter((analysisItem) => analysisItem.status === "ready").length;
  const missingCount = analysisItems.filter((analysisItem) => analysisItem.status === "missing").length;
  const overallStatus = boundedScore >= 80 && missingCount === 0
    ? "complete"
    : boundedScore >= 45
      ? "partial"
      : "insufficient";
  const summary = overallStatus === "complete"
    ? `已具备 ${readyCount}/${analysisItems.length} 类立项前基础证据，可进入人工原文核对和方案参数整理。`
    : overallStatus === "partial"
      ? `已具备 ${readyCount}/${analysisItems.length} 类基础证据，进入方案设计前仍需补齐关键缺口。`
      : `当前仅具备 ${readyCount}/${analysisItems.length} 类基础证据，不宜直接用于方案参数设定。`;

  return {
    readinessScore: boundedScore,
    overallStatus,
    summary,
    evidenceCount: sourceCount,
    items: analysisItems,
    nextActions: Array.from(new Set(nextActions)).slice(0, 6),
    limitations: [
      "资料准备度仅反映当前公开资料的完整性，不代表药品质量、临床价值或试验成功概率。",
      "自动归并结果用于立项前初筛；方案、样本量和统计参数必须核对最新版官方原文及原始研究。",
    ],
  };
}
